import {type RefObject, useState, useSyncExternalStore} from 'react'
import {createPortal} from 'react-dom'

import type {PanelArtifact} from './ArtifactPanelContent'
import {ArtifactsSidePanelEmbedded} from './ArtifactsSidePanelEmbedded'
import {ArtifactsSidePanelOverlay} from './ArtifactsSidePanelOverlay'
import type {TabItem} from './ArtifactsTabBar'

const EMBEDDED_MIN_WIDTH = 1012

let embeddedQuery: MediaQueryList | undefined

function getQuery() {
  if (embeddedQuery) return embeddedQuery
  return (embeddedQuery = window.matchMedia(`(min-width: ${EMBEDDED_MIN_WIDTH}px)`))
}

function subscribe(notify: () => void) {
  const mq = getQuery()
  mq.addEventListener('change', notify)
  return () => mq.removeEventListener('change', notify)
}

function getEmbeddedAvailableClient() {
  return getQuery().matches
}

function getEmbeddedAvailableServer() {
  return false
}

/**
 * Whether the viewport is wide enough to dock the embedded panel. SSR-safe: the
 * server snapshot is `false`, and the panel is only ever shown after a
 * post-hydration user interaction so there is no hydration mismatch.
 */
function useEmbeddedAvailable() {
  return useSyncExternalStore(subscribe, getEmbeddedAvailableClient, getEmbeddedAvailableServer)
}

type ArtifactsPanelShellProps = {
  open: boolean
  onClose: () => void
  tabs: TabItem[]
  activeId: string | null
  activeArtifact: PanelArtifact | null
  onActivate: (id: string) => void
  onPromoteOverflowTab: (id: string) => void
  onCloseTab?: (id: string) => void
  returnFocusRef: RefObject<HTMLElement | null>
}

/**
 * Renders the artifacts panel as an embedded side panel docked to the right
 * edge of the viewport (content reserves space for it via the panel-reservation
 * rules in IssueViewer.module.css). The panel is portalled to `document.body`
 * so its `position: fixed` resolves against the viewport rather than the
 * issue-viewer container, which establishes a fixed containing block via
 * `container-type`. On viewports narrower than 1012px it falls back to a modal
 * overlay panel.
 */
export function ArtifactsPanelShell(props: ArtifactsPanelShellProps) {
  const embeddedAvailable = useEmbeddedAvailable()

  // Latch that flips on the first open and never resets. Until the panel is
  // opened at least once we render nothing, so the embedded panel's
  // ResizeObservers and scroll/resize listeners aren't mounted for the lifetime
  // of the issue view when the user never opens an artifact. Once opened we keep
  // it mounted so the CSS slide-out transition can run on close.
  const [hasOpened, setHasOpened] = useState(false)
  if (props.open && !hasOpened) setHasOpened(true)

  if (embeddedAvailable && typeof document !== 'undefined') {
    if (!hasOpened) return null
    return createPortal(<ArtifactsSidePanelEmbedded {...props} />, document.body)
  }

  // The overlay's SidePanel locks body scroll on mount, so only mount it while
  // open. (The embedded variant above has no such side effect.)
  if (!props.open) return null

  return <ArtifactsSidePanelOverlay {...props} />
}
